import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  FaCheck,
  FaShieldAlt,
  FaDownload,
  FaIdCard,
  FaHeart,
  FaArrowRight,
  FaUser,
  FaFileInvoice,
} from "react-icons/fa";

import "./PaymentSuccess.css";

// ==========================================================
// LOCAL STORAGE HELPER
// ==========================================================

function getLocalStorageObject(key) {
  try {
    const value = localStorage.getItem(key);

    if (!value) {
      return {};
    }

    const parsed = JSON.parse(value);

    if (
      parsed &&
      typeof parsed === "object" &&
      !Array.isArray(parsed)
    ) {
      return parsed;
    }

    return {};
  } catch (error) {
    console.error(`Unable to read ${key}:`, error);
    return {};
  }
}

// ==========================================================
// PAYMENT SUCCESS
// ==========================================================

function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();

  // ========================================================
  // ROUTE STATE
  //
  // FIX:
  // Previously:
  // const routeState = location.state || {};
  //
  // Now routeState is memoized so ESLint does not report
  // unstable dependency warnings.
  // ========================================================

  const routeState = useMemo(() => {
    return location.state || {};
  }, [location.state]);

  // ========================================================
  // LOCAL STORAGE DATA
  // ========================================================

  const savedRegistrationData = useMemo(
    () => getLocalStorageObject("registrationData"),
    []
  );

  const savedFormData = useMemo(
    () => getLocalStorageObject("formData"),
    []
  );

  const savedPaymentData = useMemo(
    () => getLocalStorageObject("paymentData"),
    []
  );

  // ========================================================
  // FORM DATA
  // ========================================================

  const formData = useMemo(() => {
    const stateFormData = routeState.formData || {};

    return {
      ...savedFormData,
      ...savedRegistrationData,
      ...stateFormData,
    };
  }, [
    savedFormData,
    savedRegistrationData,
    routeState,
  ]);

  // ========================================================
  // PAYMENT DATA
  // ========================================================

  const paymentData = useMemo(() => {
    const statePaymentData =
      routeState.paymentData || {};

    return {
      ...savedPaymentData,
      ...statePaymentData,
    };
  }, [
    savedPaymentData,
    routeState,
  ]);

  // ========================================================
  // MEMBER NAME VALIDATION
  // ========================================================

  const isValidMemberName = (value) => {
    if (typeof value !== "string") {
      return false;
    }

    const name = value.trim();

    if (!name) {
      return false;
    }

    const invalidNames = [
      "Member Name",
      "Member",
      "Enter Member Name",
      "Enter Full Name",
      "Full Name",
      "Name",
    ];

    return !invalidNames.includes(name);
  };

  // ========================================================
  // MEMBER NAME
  // ========================================================

  const memberName =
    [
      formData.fullName,
      formData.memberName,
      formData.name,
      paymentData.fullName,
      paymentData.memberName,
      localStorage.getItem("memberName"),
    ].find(isValidMemberName) || "Member";

  // ========================================================
  // MOBILE NUMBER
  // ========================================================

  const mobileNumber =
    [
      formData.mobileNumber,
      formData.mobile,
      formData.phoneNumber,
      paymentData.mobileNumber,
      localStorage.getItem("mobileNumber"),
    ].find(
      (value) =>
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
    ) || "Not Available";

  // ========================================================
  // PAYMENT AMOUNT
  // ========================================================

  const amount =
    Number(paymentData.amount) || 1299;

  // ========================================================
  // PAYMENT DATE OBJECT
  // ========================================================

  const paymentDateObject = useMemo(() => {
    const suppliedDate =
      paymentData.paymentDate;

    if (suppliedDate) {
      const parsedDate =
        new Date(suppliedDate);

      if (
        !Number.isNaN(
          parsedDate.getTime()
        )
      ) {
        return parsedDate;
      }
    }

    return new Date();
  }, [
    paymentData.paymentDate,
  ]);

  // ========================================================
  // PAYMENT DATE
  // ========================================================

  const paymentDate =
    paymentData.paymentDate ||
    paymentDateObject.toLocaleString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );

  // ========================================================
  // MEMBERSHIP VALID TILL
  //
  // 3 YEARS MEMBERSHIP
  //
  // Example:
  //
  // Payment:
  // 30 Aug 2026
  //
  // Valid Till:
  // 29 Aug 2029
  // ========================================================

  const validTill = useMemo(() => {
    const date =
      new Date(paymentDateObject);

    date.setFullYear(
      date.getFullYear() + 3
    );

    date.setDate(
      date.getDate() - 1
    );

    return date.toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  }, [
    paymentDateObject,
  ]);

  // ========================================================
  // MEMBERSHIP TYPE
  // ========================================================

  const membershipType =
    "3 Years Membership";

  // ========================================================
  // MEMBERSHIP ID
  // ========================================================

  const membershipId =
    paymentData.membershipId ||
    localStorage.getItem(
      "membershipId"
    ) ||
    `APNBM${Date.now()
      .toString()
      .slice(-8)}`;

  // ========================================================
  // TRANSACTION ID
  // ========================================================

  const transactionId =
    paymentData.transactionId ||
    `PAY${Date.now()
      .toString()
      .slice(-10)}`;

  // ========================================================
  // ORDER ID
  // ========================================================

  const orderId =
    paymentData.orderId ||
    `ORDER${Date.now()
      .toString()
      .slice(-8)}`;

  // ========================================================
  // PAYMENT METHOD
  // ========================================================

  const paymentMethod =
    paymentData.paymentMethod ||
    "UPI";

  // ========================================================
  // PAYMENT GATEWAY
  // ========================================================

  const paymentGateway =
    paymentData.paymentGateway ||
    "Razorpay";

  // ========================================================
  // AMOUNT IN WORDS
  // ========================================================

  const amountInWords = useMemo(() => {
    if (amount === 1299) {
      return (
        "One Thousand Two Hundred " +
        "Ninety Nine Only"
      );
    }

    if (amount === 1199) {
      return (
        "One Thousand One Hundred " +
        "Ninety Nine Only"
      );
    }

    return `Rupees ${amount} Only`;
  }, [amount]);

  // ========================================================
  // SAVE FINAL MEMBER + PAYMENT DATA
  // ========================================================

  useEffect(() => {
    const finalMemberData = {
      memberName,

      fullName:
        formData.fullName ||
        memberName,

      mobileNumber:
        String(mobileNumber),

      membershipId,

      membershipType,

      validTill,

      paymentAmount:
        amount,

      paymentMethod,

      paymentGateway,

      transactionId,

      orderId,

      paymentDate,

      membershipStatus:
        "Active",

      paymentStatus:
        "Paid",

      claimStatus:
        "No Claim",
    };

    // ======================================================
    // SAVE MEMBER DATA
    // ======================================================

    localStorage.setItem(
      "memberData",
      JSON.stringify(
        finalMemberData
      )
    );

    // ======================================================
    // SAVE BASIC MEMBER DATA
    // ======================================================

    localStorage.setItem(
      "memberName",
      memberName
    );

    localStorage.setItem(
      "mobileNumber",
      String(mobileNumber)
    );

    localStorage.setItem(
      "membershipId",
      membershipId
    );

    // ======================================================
    // SAVE FINAL PAYMENT DATA
    // ======================================================

    const finalPaymentData = {
      ...paymentData,

      amount,

      membershipType,

      validTill,

      membershipId,

      transactionId,

      orderId,

      paymentDate,

      paymentMethod,

      paymentGateway,

      paymentStatus:
        "SUCCESS",
    };

    localStorage.setItem(
      "paymentData",
      JSON.stringify(
        finalPaymentData
      )
    );
  }, [
    memberName,
    formData.fullName,
    mobileNumber,
    membershipId,
    membershipType,
    validTill,
    amount,
    paymentMethod,
    paymentGateway,
    transactionId,
    orderId,
    paymentDate,
    paymentData,
  ]);

  // ========================================================
  // NAVIGATION
  // ========================================================

  const goTo = (path) => {
    navigate(path);
  };

  // ========================================================
  // DASHBOARD
  // ========================================================

  const handleGoToDashboard = () => {
    navigate("/dashboard");
  };

  // ========================================================
  // DOWNLOAD ID CARD
  // ========================================================

  const handleDownloadId = () => {
    alert(
      `Membership ID Card\n\n` +
      `Member Name: ${memberName}\n` +
      `Membership ID: ${membershipId}\n` +
      `Membership Type: ${membershipType}\n` +
      `Valid Till: ${validTill}`
    );
  };

  // ========================================================
  // DOWNLOAD RECEIPT
  // ========================================================

  const handleDownloadReceipt = () => {
    alert(
      `Payment Receipt\n\n` +
      `Member Name: ${memberName}\n` +
      `Amount: ₹${amount}\n` +
      `Transaction ID: ${transactionId}\n` +
      `Order ID: ${orderId}`
    );
  };

  // ========================================================
  // UI
  // ========================================================

  return (
    <div className="payment-success-page">

      {/* ====================================================
          HEADER
      ==================================================== */}

      <header className="success-header">

        <div className="success-header-inner">

          {/* BRAND */}

          <button
            type="button"
            className="success-brand"
            onClick={() => goTo("/")}
          >

            <div className="success-logo">

              <img
                src="/images/apnb-logo.png"
                alt="APNB Logo"
              />

            </div>

            <div className="success-brand-text">

              <strong>
                APNB SEVA SAMITHI
              </strong>

              <span>
                & VIDYA CHARITABLE TRUST
              </span>

              <small>
                Serve The Community. Secure The Future.
              </small>

            </div>

          </button>

          {/* NAVIGATION */}

          <nav className="success-navigation">

            <button
              type="button"
              onClick={() => goTo("/")}
            >
              HOME
            </button>

            <button
              type="button"
              onClick={() => goTo("/about")}
            >
              ABOUT US
            </button>

            <button
              type="button"
              onClick={() => goTo("/membership")}
            >
              MEMBERSHIP
            </button>

            <button
              type="button"
              onClick={() => goTo("/benefits")}
            >
              BENEFITS
            </button>

            <button
              type="button"
              onClick={() => goTo("/news")}
            >
              NEWS & EVENTS
            </button>

            <button
              type="button"
              onClick={() => goTo("/downloads")}
            >
              DOWNLOADS
            </button>

            <button
              type="button"
              onClick={() => goTo("/contact")}
            >
              CONTACT
            </button>

            <button
              type="button"
              className="success-join-button"
              onClick={() => goTo("/register")}
            >
              <FaUser />
              JOIN NOW
            </button>

          </nav>

        </div>

      </header>

      {/* ====================================================
          MAIN
      ==================================================== */}

      <main className="success-main">

        {/* ==================================================
            SUCCESS CARD
        ================================================== */}

        <section className="success-card">

          <div className="success-message">

            <div className="success-check">
              <FaCheck />
            </div>

            <h1>
              Payment Successful!
            </h1>

            <p>
              Thank you! Your membership payment
              has been received successfully.
            </p>

            <p>
              You are now a valued member of
              APNB Seva Samithi & Vidya Charitable Trust.
            </p>

            <div className="transaction-badge">

              <FaShieldAlt />

              <span>
                Transaction ID:
              </span>

              <strong>
                {transactionId}
              </strong>

            </div>

          </div>

          {/* ==================================================
              DETAILS
          ================================================== */}

          <div className="success-details-grid">

            {/* PAYMENT AMOUNT */}

            <div className="success-column amount-column">

              <h3>
                Payment Amount
              </h3>

              <div className="success-amount">

                ₹
                {amount.toLocaleString(
                  "en-IN",
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}

              </div>

              <p className="amount-words">
                ({amountInWords})
              </p>

              <div className="success-payment-icon">
                <FaCheck />
              </div>

            </div>

            {/* PAYMENT DETAILS */}

            <div className="success-column">

              <h3>
                Payment Details
              </h3>

              <div className="success-detail-row">

                <span>
                  Payment Date
                </span>

                <strong>
                  {paymentDate}
                </strong>

              </div>

              <div className="success-detail-row">

                <span>
                  Payment Method
                </span>

                <strong>
                  {paymentMethod}
                </strong>

              </div>

              <div className="success-detail-row">

                <span>
                  Payment Gateway
                </span>

                <strong>
                  {paymentGateway}
                </strong>

              </div>

              <div className="success-detail-row">

                <span>
                  Transaction ID
                </span>

                <strong>
                  {transactionId}
                </strong>

              </div>

              <div className="success-detail-row">

                <span>
                  Order ID
                </span>

                <strong>
                  {orderId}
                </strong>

              </div>

            </div>

            {/* MEMBERSHIP DETAILS */}

            <div className="success-column">

              <h3>
                Membership Details
              </h3>

              <div className="success-detail-row">

                <span>
                  Member Name
                </span>

                <strong>
                  {memberName}
                </strong>

              </div>

              <div className="success-detail-row">

                <span>
                  Mobile Number
                </span>

                <strong>
                  {mobileNumber}
                </strong>

              </div>

              <div className="success-detail-row">

                <span>
                  Membership ID
                </span>

                <strong>
                  {membershipId}
                </strong>

              </div>

              <div className="success-detail-row">

                <span>
                  Membership Type
                </span>

                <strong>
                  {membershipType}
                </strong>

              </div>

              <div className="success-detail-row">

                <span>
                  Valid Till
                </span>

                <strong>
                  {validTill}
                </strong>

              </div>

            </div>

          </div>

          {/* ==================================================
              INFORMATION
          ================================================== */}

          <div className="success-info-box">

            <div className="success-info-icon">
              <FaShieldAlt />
            </div>

            <div>

              <strong>
                Your payment is completed and your
                membership is activated successfully.
              </strong>

              <p>
                You can now download your Membership
                ID card and payment receipt.
              </p>

            </div>

          </div>

        </section>

        {/* ==================================================
            WHAT'S NEXT
        ================================================== */}

        <section className="whats-next-section">

          <h2>
            What's Next?
          </h2>

          <div className="next-grid">

            {/* ID CARD */}

            <div className="next-card">

              <div className="next-icon green">
                <FaIdCard />
              </div>

              <h3>
                Download ID Card
              </h3>

              <p>
                Download your digital membership ID card.
              </p>

              <button
                type="button"
                onClick={handleDownloadId}
                className="next-button green-button"
              >
                DOWNLOAD ID CARD
                <FaDownload />
              </button>

            </div>

            {/* RECEIPT */}

            <div className="next-card">

              <div className="next-icon blue">
                <FaFileInvoice />
              </div>

              <h3>
                Download Receipt
              </h3>

              <p>
                Download your payment receipt for reference.
              </p>

              <button
                type="button"
                onClick={handleDownloadReceipt}
                className="next-button blue-button"
              >
                DOWNLOAD RECEIPT
                <FaDownload />
              </button>

            </div>

            {/* BENEFITS */}

            <div className="next-card">

              <div className="next-icon purple">
                <FaHeart />
              </div>

              <h3>
                Explore Benefits
              </h3>

              <p>
                Explore all the benefits of your membership.
              </p>

              <button
                type="button"
                onClick={() => goTo("/benefits")}
                className="next-button purple-button"
              >
                VIEW BENEFITS
                <FaArrowRight />
              </button>

            </div>

            {/* DASHBOARD */}

            <div className="next-card">

              <div className="next-icon orange">
                <FaFileInvoice />
              </div>

              <h3>
                Go to Dashboard
              </h3>

              <p>
                Manage your profile, claims and
                other services.
              </p>

              <button
                type="button"
                onClick={handleGoToDashboard}
                className="next-button orange-button"
              >
                GO TO DASHBOARD
                <FaArrowRight />
              </button>

            </div>

          </div>

        </section>

      </main>

      {/* ====================================================
          FOOTER
      ==================================================== */}

      <footer className="success-footer">

        <div className="footer-brand">

          <img
            src="/images/apnb-logo.png"
            alt="APNB Logo"
          />

          <div>

            <strong>
              APNB SEVA SAMITHI
            </strong>

            <span>
              & VIDYA CHARITABLE TRUST
            </span>

            <small>
              Serve The Community. Secure The Future.
            </small>

          </div>

        </div>

        <div className="footer-links">

          <h4>
            Quick Links
          </h4>

          <button
            type="button"
            onClick={() => goTo("/about")}
          >
            About Us
          </button>

          <button
            type="button"
            onClick={() => goTo("/membership")}
          >
            Membership
          </button>

          <button
            type="button"
            onClick={() => goTo("/benefits")}
          >
            Benefits
          </button>

          <button
            type="button"
            onClick={() => goTo("/news")}
          >
            News & Events
          </button>

          <button
            type="button"
            onClick={() => goTo("/contact")}
          >
            Contact Us
          </button>

        </div>

        <div className="footer-links">

          <h4>
            Important Links
          </h4>

          <button
            type="button"
            onClick={() => goTo("/downloads")}
          >
            Downloads
          </button>

          <button type="button">
            Privacy Policy
          </button>

          <button type="button">
            Terms & Conditions
          </button>

          <button type="button">
            Refund Policy
          </button>

          <button type="button">
            FAQ
          </button>

        </div>

        <div className="footer-contact">

          <h4>
            Contact Us
          </h4>

          <p>
            ☎ +91 91234 56789
          </p>

          <p>
            ✉ info@apnbtrust.org
          </p>

          <p>
            📍 D.No: 10-3-45, Guntur,
            <br />
            Andhra Pradesh - 522001
          </p>

        </div>

      </footer>

      <div className="footer-bottom">

        <span>
          © 2026 APNB Seva Samithi & Vidya Charitable Trust.
          All Rights Reserved.
        </span>

        <span>
          Designed with ❤️ for our members
        </span>

      </div>

    </div>
  );
}

export default PaymentSuccess;